/* eslint-disable no-shadow */
/**
 *
 * This module exports Scalyr-defined constants. Constants may also be defined in modules with module scope. Constants
 * defined here must be uniquely named across project scope and should be imported under the collection K:
 *
 *    import * as K from 'common/constants';
 */

// WARNING: this module is very heavily imported and importing here is likely to cause circular dependencies. It should
// only import from third parties or from "export-only" modules (such as extracted types modules)
import { invert } from 'lodash';

// Relative path needed since these files are imported for E2E tests.
import { SortOrder, Theme, SystemField } from './graphqlTypes';

// General

export const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

export const PRODUCT_NAME = 'DataSet';

// In the React hooks ecosystem, it is useful to have stable references to these values so that React does not believe
// that they have changed from render to render.
export const EMPTY_ARRAY    = [];
export const EMPTY_OBJECT   = {};
export const EMPTY_SET      = new Set();
export const EMPTY_FUNCTION = (): void => {};

export const MS_PER_MIN = 60 * 1000;  // milliseconds per minute
export const NS_PER_MS = 1000000;     // nanoseconds per millisecond
export const ONE_HOUR = 60 * 60 * 1000;

export const DEFAULT_FEATURE_PERMISSIONS = EMPTY_OBJECT;

export enum SIZE {
  SMALL  = 'Small',
  MEDIUM = 'Medium',
  LARGE  = 'Large',
}

export enum ALIGNMENT {
  START  = 'start',
  CENTER = 'center',
  END    = 'end',
}

export enum PLACEMENT {
  BEGINNING,
  MIDDLE,
  END,
}

export enum MATH_OPERATOR {
  ADD      = '+',
  SUBTRACT = '-',
  MULTIPLY = '*',
  DIVIDE   = '/',
  MODULO   = '%',
}

// generic actions a user may take relative to a bit of data
export enum ACTION {
  SAVE   = 'save',
  DELETE = 'delete',
  EDIT   = 'edit',
  COPY   = 'copy',
}

export enum KEYBOARD_KEY {
  ARROW_DOWN = 'ArrowDown',
  ENTER      = 'Enter',
  ESCAPE     = 'Escape',
  ARROW_UP   = 'ArrowUp',
}

export enum KEY_CODE {
  DOWN_ARROW = 40,
  ENTER      = 13,
  UP_ARROW   = 38,
  C          = 67,
}

export const REGEX = {
  LINE_RETURN: /\r\n|\r|\n/,
};

// date components; these are also the setter names used by Moment
export enum DATE {
  MILLISECOND = 'millisecond',
  SECOND      = 'second',
  MINUTE      = 'minute',
  HOUR        = 'hour',
  WEEKDAY     = 'weekday',
  DAY         = 'day', // day of month
  WEEK        = 'week',
  MONTH       = 'month',
  YEAR        = 'year',
}

// max length for browser tab title
export const TITLE_MAX_LENGTH = 100;

// supported formats for copying/downloading result data
export enum DATA_FORMAT {
  CSV      = 'csv',
  JSON     = 'json',
  MARKDOWN = 'markdown',
  TEXT     = 'text',
}

export const DATA_FORMAT_EXTENSION = {
  [DATA_FORMAT.CSV]:      'csv',
  [DATA_FORMAT.JSON]:     'json',
  [DATA_FORMAT.MARKDOWN]: 'md',
  [DATA_FORMAT.TEXT]:     'txt',
};

export const DATA_FORMAT_CONTENT_TYPE = {
  [DATA_FORMAT.CSV]:      'text/csv',
  [DATA_FORMAT.JSON]:     'application/json',
  [DATA_FORMAT.MARKDOWN]: 'text/markdown',
  [DATA_FORMAT.TEXT]:     'text/plain',
};

// Settings

export const API_ENDPOINT = '/v2/graphql'; // Should be used across the App to communicate via the GraphQL API

export enum SETTING {
  ADDITIONAL_FIELDS                           = 'additionalFields',       // which customer-defined fields to show in log lines
  AUTO_UPDATE_SEARCH                          = 'autoUpdateSearch',       // whether to issue a search when time range or host/log has been changed
  DEFAULT_START_TIME                          = 'defaultStartTime',       // default search time range (system default is last 4 hours)
  DISPLAY_MODE                                = 'displayMode',            // controls how log lines are displayed (lines, wrapped lines, or table)
  ENABLE_HIDDEN_API_KEYS                      = 'enableHiddenApiKeys',    // used to enable extra functionality, for example, within API Keys Page
  FONT_FAMILY                                 = 'fontFamily',             // whether to use regular or monospace font for log results
  LOG_VIEW_WINDOW                             = 'logViewWindow',          // time window in hours when user views event in Original or Thread context
  OVERVIEW_AGE_LIMIT                          = 'overviewAgeLimit',       // time range in hours for host/log activity for them appear on overview page
  OVERVIEW_AGE_LIMIT_ALL                      = 'overviewAgeLimitAll',    // same as above for "show all"
  OVERVIEW_LOGS_PER_HOST                      = 'overviewLogsPerHost',    // how many logfiles to show for each host on the overview page
  SHOW_SIDEBAR                                = 'showSidebar',            // whether to show the facets sidebar
  SMART_LINKS_CONFIG                          = 'smartLinksConfig',       // configuration for matching and linking specific log line content
  SORT_ORDER                                  = 'sortOrder',              // sort order used to display events
  STRIPE_PUBLISHABLE_KEY                      = 'stripePublishableKey',   // used to handle Stripe Processing
  SYSTEM_FIELDS                               = 'systemFields',           // which system fields (time, host, etc) to show in log lines
  THEME                                       = 'theme',                  // UI theme (one of THEME)
  UNESCAPE_LOG_LINE_MESSAGE                   = 'unescapeLogLineMessage', // replace escaped line returns with real ones
  REACT_PATHS                                 = 'reactPaths',             // names of paths that will be redirected to react app
  PAGES_EXCLUDED_FOR_TRIAL_EMAIL_VERIFICATION = 'pagesExcludedForTrialEmailVerification', // names of pages to exclude for the email verification check for trial accounts
  REACT_PATHS_BLACKLIST                       = 'reactPathsBlacklist',    // names of pages that will not be redirected to react, but will be served by legacy app
}

// These settings are not part of Display Settings (`SETTINGS`), but they should probably be
export enum VIEW_SETTING {
  DISABLE_LIVE_TAIL                           = 'disableLiveTail',
  SEARCH_ALL_TEAMS_BY_DEFAULT                 = 'searchAllTeamsByDefault',
}

export const SERVER_SETTING_NAME = {
  [SETTING.ADDITIONAL_FIELDS]:                           'additionalFields',
  [SETTING.AUTO_UPDATE_SEARCH]:                          'autoUpdateSearch',
  [SETTING.DEFAULT_START_TIME]:                          'defaultSearchTimeSpan',
  [SETTING.DISPLAY_MODE]:                                'displayMode',
  [SETTING.ENABLE_HIDDEN_API_KEYS]:                      'enableHiddenApiKeys',
  [SETTING.LOG_VIEW_WINDOW]:                             'originalLogOrThreadTimeNs',
  [SETTING.OVERVIEW_AGE_LIMIT]:                          'defaultAgeLimitHoursInOverviewPage',
  [SETTING.OVERVIEW_AGE_LIMIT_ALL]:                      'showAllAgeLimitHoursInOverviewPage',
  [SETTING.OVERVIEW_LOGS_PER_HOST]:                      'logsPerHostInOverviewPage',
  [SETTING.SHOW_SIDEBAR]:                                'showLeftNavExpanded',
  [SETTING.SMART_LINKS_CONFIG]:                          'searchResultLinks',
  [SETTING.SORT_ORDER]:                                  'sortOrder',
  [SETTING.FONT_FAMILY]:                                 'fontFamily',
  [SETTING.STRIPE_PUBLISHABLE_KEY]:                      'stripePublishableKey',
  [SETTING.SYSTEM_FIELDS]:                               'systemFields',
  [SETTING.THEME]:                                       'theme',
  [SETTING.UNESCAPE_LOG_LINE_MESSAGE]:                   'unescapeLogLineMessage',
  [SETTING.REACT_PATHS]:                                 'reactPaths',
  [SETTING.REACT_PATHS_BLACKLIST]:                       'reactPathsBlacklist',
  [SETTING.PAGES_EXCLUDED_FOR_TRIAL_EMAIL_VERIFICATION]: 'pagesExcludedForTrialEmailVerification'
};

export const CLIENT_SETTING_NAME = invert(SERVER_SETTING_NAME);

// settings that affect display of log results, controlled by settings dialog
export const DISPLAY_SETTINGS = [ SETTING.ADDITIONAL_FIELDS, SETTING.DISPLAY_MODE, SETTING.SORT_ORDER, SETTING.FONT_FAMILY, SETTING.SYSTEM_FIELDS ];

export enum DISPLAY_MODE {
  LINES   = 'lines',
  WRAPPED = 'wrappedLines',
  TABLE   = 'table',
}

export enum FONT_FAMILY {
  REGULAR   = 'regular',
  MONOSPACE = 'monospace',
}

export const DEFAULT_EVENT_SORT = SortOrder.MOST_RECENT_AT_BOTTOM;
export enum SCROLL_DIRECTION {
  UP = 'UP',
  DOWN = 'DOWN',
}

export const DEFAULT_SETTINGS = {
  [SETTING.ADDITIONAL_FIELDS]:                           EMPTY_ARRAY as string[],
  [SETTING.AUTO_UPDATE_SEARCH]:                          true,
  [SETTING.DEFAULT_START_TIME]:                          '4 hours',
  [SETTING.DISPLAY_MODE]:                                DISPLAY_MODE.LINES,
  [SETTING.FONT_FAMILY]:                                 FONT_FAMILY.MONOSPACE,
  [SETTING.LOG_VIEW_WINDOW]:                             600000000000,
  [SETTING.OVERVIEW_AGE_LIMIT]:                          24,
  [SETTING.OVERVIEW_AGE_LIMIT_ALL]:                      24 * 30,
  [SETTING.OVERVIEW_LOGS_PER_HOST]:                      5,
  [SETTING.SHOW_SIDEBAR]:                                true,
  [SETTING.SMART_LINKS_CONFIG]:                          EMPTY_ARRAY,
  [SETTING.SORT_ORDER]:                                  DEFAULT_EVENT_SORT,
  [SETTING.SYSTEM_FIELDS]:                               [ SystemField.timestamp, SystemField.host, SystemField.logfile ],
  [SETTING.THEME]:                                       'sentinel_one_dark',
  [SETTING.UNESCAPE_LOG_LINE_MESSAGE]:                   false,
  [SETTING.ENABLE_HIDDEN_API_KEYS]:                      false,
  [SETTING.STRIPE_PUBLISHABLE_KEY]:                      'pk_test_AJi9zdkwyNWULLmjFA4W0jDj', // This is Stripe public test token. It doesn't allow the use of real cards, only test ones.
  [SETTING.PAGES_EXCLUDED_FOR_TRIAL_EMAIL_VERIFICATION]: ["ssoInvite", "acceptLinkInvite", "newLinkAccount", "emailVerificationRequired", "verifyEmail"],
  [SETTING.REACT_PATHS]:                                 EMPTY_ARRAY as string[],
  [SETTING.REACT_PATHS_BLACKLIST]:                       EMPTY_ARRAY as string[],
};

export const DEFAULT_VIEW_SETTINGS = {
  [VIEW_SETTING.DISABLE_LIVE_TAIL]:                      false,
  [VIEW_SETTING.SEARCH_ALL_TEAMS_BY_DEFAULT]:            false,
};

export enum PREFERENCE {
  TIMEZONE  = 'timezone',
  HOMEPAGES = 'homepages',
};

export const DEFAULT_PREFERENCES = {
  [PREFERENCE.TIMEZONE]: null,
  [PREFERENCE.HOMEPAGES]: [],
};

// API keys
export const LOG_KEY_TYPE        = 'LOG';
export const CONFIG_KEY_TYPE     = 'CONFIG';
export const LOG_ACCESS_TITLE    = 'Log Access Keys';
export const CONFIG_ACCESS_TITLE = 'Configuration Access Keys';

// Event page modes
export enum EVENT_MODE {
  DISTRIBUTION     = 'histogram',
  GRAPH            = 'graph',
  LOG              = 'log',
  LIVE_TAIL        = 'liveTail',
  SEARCH           = 'search',      // combined search
  TRACE            = 'trace',
}

// URLs that are known to the Scalyr web app but not handled by the app itself
export enum LINK {
  RELEASE_NOTES       = 'https://www.scalyr.com/releasenotes/',
  // TODO: '/support' is currently not an internal route for this app. If we want to remove our dependency
  //  on a route-by-route blacklist or whitelist, this should perhaps not be relative.
  SUPPORT             = '/support',
  SIGN_UP             = 'https://www.dataset.com/try-dataset/',
}

// Routes
export enum ROUTE {
  ACCEPT_LINK_INVITE  = '/acceptLinkInvite',
  ALERTS              = '/alerts',
  ALERT               = '/alert',
  BULK_EXPORTS        = '/bulkExports',
  CHANGE_PASSWORD     = '/changePassword',
  CONFIG              = '/files',
  CONFIG_FILE         = '/file',
  DASHBOARD           = '/dash',
  DASHBOARD_LIST      = '/dashboards',
  DISTRIBUTION        = '/events?mode=histogram',
  EMAIL_VERIFICATION  = '/emailVerificationRequired',
  EVENTS              = '/events',
  EXAMPLES            = '/examples',
  GRAPH               = '/events?mode=graph',
  HELP                = '/help',
  KEYS                = '/keys',
  LABS                = '/labs',
  LIVE_TAIL           = '/events?mode=liveTail',
  LOG_IN              = '/login',
  // LOG_OUT             = '/Logout', // Not implemented
  LOG_PROCESSING      = '/logProcessing',
  LOG_START           = '/logStart',
  LOG_PARSE_TESTER    = '/logParseTester',
  LOG_IMPORT_TESTER   = '/logImportTester',
  MANAGE_TEAMS        = '/linkAccount',
  MANAGE_GROUPS       = '/groups',
  MANAGE_USERS        = '/userAccounts',
  MONITORS_PAGE       = '/monitors',
  PAGE_NOT_FOUND      = '/404',
  PARSERS             = '/parsers',
  PARSER              = '/parser',
  PASSWORD_RESET      = '/PasswordReset',
  PLAN                = '/plan',
  POWER_QUERY         = '/query',
  ROOT                = '/',
  SECRETS             = '/secrets',
  SSO_INVITE          = '/ssoInvite',
  SIGN_UP_INTERNAL    = '/signupInternal',
  SIGN_UP_FORM        = '/signUpForm',
  SLACK_OAUTH         = '/slackoauth',
  TRACE               = '/events?mode=trace',
  TRY_SCALYR_FORM     = '/tryScalyrForm',
  UPDATE_CREDIT_CARD  = '/updateCreditCard',
  VERIFY_EMAIL        = '/verifyEmail',
}

export enum ROUTE_NAME {
  LOGS           = 'Logs',
  SEARCH         = 'Search',
  DASHBOARD_LIST = 'View All Dashboards',
  ALERTS         = 'Alerts'
}

// Routes available to an unauthenticated user
export enum PUBLIC_ROUTE {
  ACCEPT_LINK_INVITE  = '/acceptLinkInvite',
  HELP                = '/help',
  PAGE_NOT_FOUND      = '/404',
  PASSWORD_RESET      = '/PasswordReset',
  SIGN_UP_INTERNAL    = '/signupInternal',
  SSO_INVITE          = '/ssoInvite',
}

// TBD - FRONT-7307 Newly create Dataset domains
export const NEW_DOMAINS = [
  'app.intus1.scalyr.com',
  'app.us1.dataset.com',
  'app.eu1.dataset.com',
  'app.ca1.dataset.com',
  'xdr.us1.sentinelone.net',
  'xdr.eu1.sentinelone.net',
  'xdr.ca1.sentinelone.net',
];

// Sorted list of supported URL parameters. This is a comprehensive list that includes every param handled by the legacy app.
export enum URL_PARAM {
  ADDITIONAL_FIELDS                 = 'showAdditionalFields',     // log lines display setting; facets to show
  AUTH_ORIGINAL_URL                 = 'originalUrl',              // used to identify where to redirect after login
  BAR_WIDTH                         = 'barWidth',
  BREAKDOWN_FACET                   = 'breakdownFacet',
  CENTERED                          = 'centered',
  CODE                              = 'code',
  COMPARE_TIME_OFFSET               = 'compareTimeOffset',
  CONTINUATION_TOKEN                = 'continuationToken',
  DASHBOARD_PARAMS                  = 'params',
  DASHBOARD_FILTER                  = 'dashboardFilter',
  DASHBOARD_FILTERS                 = 'dashboardFilters',
  DASHBOARD_NAME                    = 'page',                     // name of the dashboard to show
  DASHBOARD_VIEW_GRAPH              = 'viewGraph',
  DASHBOARD_ACTIVE_TAB              = 'activeTab',
  DATA_SOURCE                       = 'dataSource',
  DISPLAY_MESSAGE                   = 'displayMessage',
  DISPLAY_MODE                      = 'displayMode',              // log lines display setting; how to show events
  ERR                               = 'err',                      // error param returned from server
  ERR_MSG                           = 'errMsg',                   // custom error message returned from server
  END_TIME                          = 'endTime',
  FACET                             = 'facet',
  FACET_FUNCTION                    = 'facetFunction',
  FILTER                            = 'filter',
  GRAPH_STYLE                       = 'graphStyle',
  HIDE_GRAPHS                       = 'hideGraphs',
  HIDE_PLOTS                        = 'hidePlots',
  HIGHLIGHT_EVENT                   = 'highlightEvent',
  ID                                = 'id',                       // alert ID for details page
  INCLUSIVE_START                   = 'inclusiveStart',
  INVITE_TOKEN                      = 'inviteToken',
  KUBERNETES_CLUSTER                = 'k8sCluster',
  KUBERNETES_CONTROLLER             = 'k8sController',
  KUBERNETES_CRON_JOB               = 'k8sCronJob',
  KUBERNETES_DAEMON_SET             = 'k8sDaemonSet',
  KUBERNETES_DEPLOYMENT             = 'k8sDeployment',
  KUBERNETES_JOB                    = 'k8sJob',
  KUBERNETES_KIND                   = 'k8sKind',
  KUBERNETES_REPLICA_SET            = 'k8sReplicaSet',
  KUBERNETES_REPLICATION_CONTROLLER = 'k8sReplicationController',
  KUBERNETES_STATEFUL_SET           = 'k8sStatefulSet',
  LINE_SMOOTHING                    = 'lineSmoothing',
  LOG                               = 'log',                      // logfile
  LOG_SOURCE                        = 'logSource',                // server host
  MAX_PIE_SLICES                    = 'maxPieSlices',
  MODE                              = 'mode',                     // routing; differentiates among events pages
  NUM_BARS                          = 'numBars',
  ORIGINAL_URL                      = 'originalUrl',              // the url to return to from editing a config file
  PATH                              = 'path',
  PARSER                            = 'parser',
  PIE_STYLE                         = 'pieStyle',
  POST_GRAPH_TO_SLACK_OPEN          = 'postGraphToSlackOpen',
  RESULTS_TAB                       = 'resultsTab',
  SHOW_GRAPHS                       = 'showGraphs',
  SHOW_OLD                          = 'showOld',                  // overview page
  SHOW_PLOTS                        = 'showPlots',
  START_TIME                        = 'startTime',
  STYLE                             = 'style',
  STATE                             = 'state',
  SYSTEM_PREFS                      = 'showSystemPrefs',          // log lines display setting; system fields to show
  TAB                               = 'tab',                      // overview page
  TEAM_EMAILS                       = 'teamEmails',               // cross-team search
  TEAM_TOKEN                        = 'teamToken',
  TOKEN                             = 'token',                    // verifyEmail, changePassword token
  THEME                             = 'theme',                    // UI theme ('sentinel_one' or 'sentinel_one_dark')
  TOKEN_CENTERED                    = 'tokenCentered',
  USER_EMAIL                        = 'userEmail',                // user email param, returned from server
  UTM_CAMPAIGN                      = 'utm_campaign',             // utm_* are Marketing Campaign params parsed on the TRY_SCALYR_FORM, SIGN_UP_FORM pages
  UTM_CONTENT                       = 'utm_content',
  UTM_MEDIUM                        = 'utm_medium',
  UTM_SOURCE                        = 'utm_source',
  UTM_TERM                          = 'utm_term',
  GCLID                             = 'gclid',
  VIEW                              = 'view',
  WEBSITE_OPT_IN                    = 'website_opt_in',
  WALL_VIEW                         = 'wallView',                 // dashboard; removes navbar
  Y_SCALE                           = 'yScale',
}

// query terms that map to filter facets (and are also URL params)
// note: these are a subset of URL_PARAM, but I didn't find a good TS way to express and use that fact
export enum QUERY_TERM {
  KUBERNETES_CLUSTER                = URL_PARAM.KUBERNETES_CLUSTER,
  KUBERNETES_CONTROLLER             = URL_PARAM.KUBERNETES_CONTROLLER,
  KUBERNETES_CRON_JOB               = URL_PARAM.KUBERNETES_CRON_JOB,
  KUBERNETES_DAEMON_SET             = URL_PARAM.KUBERNETES_DAEMON_SET,
  KUBERNETES_DEPLOYMENT             = URL_PARAM.KUBERNETES_DEPLOYMENT,
  KUBERNETES_JOB                    = URL_PARAM.KUBERNETES_JOB,
  KUBERNETES_KIND                   = URL_PARAM.KUBERNETES_KIND,
  KUBERNETES_REPLICA_SET            = URL_PARAM.KUBERNETES_REPLICA_SET,
  KUBERNETES_REPLICATION_CONTROLLER = URL_PARAM.KUBERNETES_REPLICATION_CONTROLLER,
  KUBERNETES_STATEFUL_SET           = URL_PARAM.KUBERNETES_STATEFUL_SET,
  LOG                               = URL_PARAM.LOG,
  LOG_SOURCE                        = URL_PARAM.LOG_SOURCE,
}

// facet names that have corresponding query terms
export enum FACET_TERM {
  KUBERNETES_CLUSTER                = 'k8s-cluster',
  KUBERNETES_CONTROLLER             = 'k8s-controller',
  KUBERNETES_CRON_JOB               = 'k8s-cron-job',
  KUBERNETES_DAEMON_SET             = 'k8s-daemon-set',
  KUBERNETES_DEPLOYMENT             = 'k8s-deployment',
  KUBERNETES_JOB                    = 'k8s-job',
  KUBERNETES_KIND                   = 'k8s-kind',
  KUBERNETES_REPLICA_SET            = 'k8s-replica-set',
  KUBERNETES_REPLICATION_CONTROLLER = 'k8s-replication-controller',
  KUBERNETES_STATEFUL_SET           = 'k8s-stateful-set',
  LOG                               = 'logfile',
  LOG_SOURCE                        = 'serverHost',
}

// Kubernetes controller types (kinds)
export const K8S_CONTROLLERS = [
  FACET_TERM.KUBERNETES_CONTROLLER,
  FACET_TERM.KUBERNETES_CRON_JOB,
  FACET_TERM.KUBERNETES_DAEMON_SET,
  FACET_TERM.KUBERNETES_DEPLOYMENT,
  FACET_TERM.KUBERNETES_JOB,
  FACET_TERM.KUBERNETES_REPLICA_SET,
  FACET_TERM.KUBERNETES_REPLICATION_CONTROLLER,
  FACET_TERM.KUBERNETES_STATEFUL_SET,
];

// map from query term to its facet field within a filter
export const QUERY_TERM_TO_FACET_TERM: Record<QUERY_TERM, FACET_TERM> = {
  [QUERY_TERM.KUBERNETES_CLUSTER]:                FACET_TERM.KUBERNETES_CLUSTER,
  [QUERY_TERM.KUBERNETES_CONTROLLER]:             FACET_TERM.KUBERNETES_CONTROLLER,
  [QUERY_TERM.KUBERNETES_CRON_JOB]:               FACET_TERM.KUBERNETES_CRON_JOB,
  [QUERY_TERM.KUBERNETES_DAEMON_SET]:             FACET_TERM.KUBERNETES_DAEMON_SET,
  [QUERY_TERM.KUBERNETES_DEPLOYMENT]:             FACET_TERM.KUBERNETES_DEPLOYMENT,
  [QUERY_TERM.KUBERNETES_JOB]:                    FACET_TERM.KUBERNETES_JOB,
  [QUERY_TERM.KUBERNETES_KIND]:                   FACET_TERM.KUBERNETES_KIND,
  [QUERY_TERM.KUBERNETES_REPLICA_SET]:            FACET_TERM.KUBERNETES_REPLICA_SET,
  [QUERY_TERM.KUBERNETES_REPLICATION_CONTROLLER]: FACET_TERM.KUBERNETES_REPLICATION_CONTROLLER,
  [QUERY_TERM.KUBERNETES_STATEFUL_SET]:           FACET_TERM.KUBERNETES_STATEFUL_SET,
  [QUERY_TERM.LOG]:                               FACET_TERM.LOG,
  [QUERY_TERM.LOG_SOURCE]:                        FACET_TERM.LOG_SOURCE,
};
// map system fields with display strings
export const SystemFieldText: Record<SystemField, string> = {
  [SystemField.date]:       'Date',
  [SystemField.timestamp]:  'Time',
  [SystemField.host]:       'Source/Cluster',
  [SystemField.source]:     'Source/Cluster', // schema has both 'host' and 'source' for some reason
  [SystemField.logfile]:    'Logfile/Deployment',
  [SystemField.rawLogLine]: 'Raw log',
};

// map from (lower-cased) facet field to corresponding query term
export const FACET_TERM_TO_QUERY_TERM = {} as Record<FACET_TERM, QUERY_TERM>;
Object.entries(QUERY_TERM_TO_FACET_TERM).forEach(([ key, value ]) => { FACET_TERM_TO_QUERY_TERM[value.toLowerCase() as FACET_TERM] = key as unknown as QUERY_TERM; });

// settings that we honor as query params
export const URL_PARAM_TO_SETTING: Record<URL_PARAM, SETTING> = {} as Record<URL_PARAM, SETTING>;
Object.values(URL_PARAM).forEach((key) => { URL_PARAM_TO_SETTING[key as URL_PARAM] = '' as SETTING; });
URL_PARAM_TO_SETTING[URL_PARAM.ADDITIONAL_FIELDS] = SETTING.ADDITIONAL_FIELDS;
URL_PARAM_TO_SETTING[URL_PARAM.DISPLAY_MODE]      = SETTING.DISPLAY_MODE;
URL_PARAM_TO_SETTING[URL_PARAM.SYSTEM_PREFS]      = SETTING.SYSTEM_FIELDS;

export const SETTING_TO_URL_PARAM: Record<SETTING, URL_PARAM> = {} as Record<SETTING, URL_PARAM>;
Object.entries(URL_PARAM_TO_SETTING).forEach(([ key, value ]) => { SETTING_TO_URL_PARAM[value as SETTING] = key as URL_PARAM; });

// Facets/fields
export enum FIELD {
  MESSAGE = 'message',
  TAG     = 'tag',
}

// number of lines (of message) to show before truncating and showing "more" link; move to config knob at some point?
export const MAX_FACET_VALUE_LINES = 10;

// Labs
export enum LAB {
  BETA_UI_RANGED_ANNOTATIONS  = 'betaUIRangedAnnotations',
  DASHBOARD_FILTERS           = 'dashboardFilters',
  HINDSIGHT                   = 'hindsight',
  MANAGE_GROUPS               = 'manageGroups',
  K8S_DASHBOARD               = 'k8sDashboard',
  PERFORMANCE_LOGGER          = 'performanceLogger',
  PIN_LOG_DETAILS             = 'pinLogDetails',
  POWER_QUERIES               = 'multiStepQuery',
  PQ_ALERTS                   = 'PQAlerts',
  SEARCH_TRACES               = 'searchTracesPage',
  WEB_VITALS_LOGGER           = 'webVitalsLogger',
}

// Logger
export const SCALYR_LOGGER = 'ScalyrLogger';

// Account type
export const DEMO    = 'DEMO'; // there is no AccountType.DEMO; providing type keeps TS from complaining
export const TRIAL   = 'TRIAL';
export const PAID    = 'PAID';
export const UNKNOWN = 'UNKNOWN';

/**
 * Component state
 *  Identifies a finite set of statuses for a component.
 *  Pattern looked up here: https://kentcdodds.com/blog/stop-using-isloading-booleans
 */
export enum COMPONENT_STATUS {
  IDLE        = 'idle',
  PENDING     = 'pending',
  RESOLVED    = 'resolved',
  REJECTED    = 'rejected'
}

/**
 * Search page
 */

export enum ICON_BY_SEVERITY {
  info = 3,
  warn,
  error,
}

export const DEFAULT_SEARCH_START = '4 hours';
export const DEFAULT_SEARCH_END   = 'NOW';

export const NUM_TOP_FACETS         = 100;
export const NUM_VALUES_PER_FACET   = 30;
export const MORE_VALUES_PER_FACET  = 200;

export const NUM_BAR_TIMELINE_SLICES  = 40;
export const NUM_LINE_TIMELINE_SLICES = 480;

export const REQUEST_BLOCK_SIZE           = 200;
export const NUM_INITIAL_LOG_ENTRIES      = 200;
export const NUM_CONTINUATION_LOG_ENTRIES = 201;

// Controls how many additional logs to keep in the DOM in each direction while scrolling, which is useful for users
// trying to manually select and copy multiple results from the DOM
export const LOG_RESULTS_OVERSCAN_COUNT = 20;

export enum SEARCH_FIELD_DATA_TYPE {
  SOURCE     = 'source',
  LOG        = 'log',
  CLUSTER    = 'cluster',
  CONTROLLER = 'controller',
}

export const MAX_SEARCH_FIELD_ITEMS = 500;

// default time range around a centered search (such as Original Log)
export const DEFAULT_CENTERED_SEARCH_WINDOW_MS = 2 * 60 * MS_PER_MIN;

// from the line-height property on the LogLine class
export const LOGLINE_HEIGHT = 20;

// additional height for the log line; the inner content is centered with flex
export const LOGLINE_EXTRA_SPACE = 6;

/**
 * Graph page
 */

export const DEFAULT_AUTO_SEARCH_GRAPH_SLICES = 480;
export const DEFAULT_AUTO_BAR_SLICES = 40; // TODO: make this dynamic based on chart pixel width

export enum SERIES_TYPES {
  LINE    = 'line',
  COLUMN  = 'column',
  SCATTER = 'scatter',
}

export enum CHART_TYPES {
  AREA        = 'area',
  STACKED     = 'stacked',       // legacy chart type for "area"
  LINE        = 'line',
  STACKED_BAR = 'stacked_bar',
  AREA_SPLINE = 'areaspline',
  SPLINE      = 'spline',
  PIE         = 'pie',
}

// Data vis types for PQ
export enum PQ_DATA_VIS_TYPES {
  TABLE       = 'table',
  PIE         = 'pie',
}

export enum PIE_CHART_TYPES {
  PIE    = 'pie',
  DONUT  = 'donut',
}

export enum LINE_OPTIONS {
  STRAIGHT_LINES = 'straightLines',
  SMOOTH_CURVES  = 'smoothCurves',
}

export enum Y_SCALE_OPTIONS {
  LINEAR      = 'linear',
  LOGARITHMIC = 'logarithmic',
}

export const AUTO_BAR_WIDTH = 'auto';
export const DEFAULT_EXPORT_GRAPH_TITLE = 'Graph of Matching Events Per Second';
export const CHART_SELECTION_ZINDEX = 7;
export const DEFAULT_COLUMN_SPACING     = 1.5;
export const FACET_DISPLAY_NAME_MAX_LEN = 18;
export const GRAPH_LEGEND_ICON_SIZE     = 15;

/**
 * Power Query page
 */

 export const PQ_DEFAULT_LIMIT = 1000;
 export const PQ_LIMIT_COMMANDS = [ 'limit', 'group', 'join', 'union', 'transpose' ];

// Dashboard
export const INTERVAL_OPTIONS = [
  '1 minute',
  '5 minutes',
  '10 minutes',
  '15 minutes',
  '30 minutes',
  '1 hour',
  '6 hours',
  '12 hours',
  '1 day',
];

export const DEFAULT_MAX_PIE_SLICES = 10; // The default maximum number of slices for a pie/donut

export enum PANEL_TYPE {
  PIE             = 'pie',
  DONUT           = 'donut',
  TABLE           = 'table',
  NUMBER          = 'number',
  MARKDOWN        = 'markdown',
  HONEYCOMB       = 'honeycomb',
}

// Overview page
export const SOURCES_TAB = 'Sources';
export const SERVICES_TAB = 'Services';

// Alerts page
export const DEFAULT_NUM_ALERTS_IN_GROUP = 1;

type FacetFunction = {
  op:           string;
  name:         string;
  description?: string;
};
type FacetFunctionsType = FacetFunction[];

export const FACET_FUNCTIONS: FacetFunctionsType = [
  { op: 'rate', name: 'count', description: 'The events per second of all matching occurrences of the filtered value. Field is not applicable in this case.' },
  { op: 'mean', name: 'mean', description: 'The average of all values in each time period. For instance, if you are graphing server response times, this will show the average response time.' },
  { op: 'min', name: 'min', description: 'The smallest value in each time period.' },
  { op: 'max', name: 'max', description: 'The largest value in each time period.' },
  { op: 'sumPerSec', name: 'sumPerSecond', description: 'Adds all values in each time period, and divides by the time span. For instance, if you are graphing the response-size field in a web access log, this will give the response bandwidth in bytes per second.' },
  { op: 'p10', name: '10th %tile', description: 'Shows the 10th percentile of all values in each time period.' },
  { op: 'p50', name: '50th %tile', description: 'Shows the 50th percentile (median) of all values in each time period.' },
  { op: 'p90', name: '90th %tile', description: 'Shows the 90th percentile (median) of all values in each time period.' },
  { op: 'p95', name: '95th %tile', description: 'Shows the 95th percentile (median) of all values in each time period.' },
  { op: 'p99', name: '99th %tile', description: 'Shows the 99th percentile (median) of all values in each time period.' },
  { op: 'p999', name: '99.9th %tile', description: 'Shows the 99.9th percentile (median) of all values in each time period.' },
];

export const [ RATE, MEAN, MIN, MAX, SUM_PER_SEC, P10, P50, P95, P99, P999 ] = FACET_FUNCTIONS;

// Used for helping filter out weighted averages
export const COUNT = 'count';

export const GROUPED_FACET_FUNCTIONS: Record<string, FacetFunctionsType> = {
  functions: [
    { op: 'mean',      name: 'mean' },
    { op: 'min',       name: 'min' },
    { op: 'max',       name: 'max' },
    { op: 'sum',       name: 'sum' },
    { op: 'sumPerSec', name: 'sumPerSec' },
  ],
  percentiles: [
    { op: 'p10',  name: '10th %tile' },
    { op: 'p50',  name: '50th %tile' },
    { op: 'p90',  name: '90th %tile' },
    { op: 'p95',  name: '95th %tile' },
    { op: 'p99',  name: '99th %tile' },
    { op: 'p999', name: '99.9th %tile' },
  ],
};

// Used for search graph facet functions
export const [ , , , SUM ] = GROUPED_FACET_FUNCTIONS.functions;

/** This is a hybrid enum based on LongOp.State, but with CANCELED and TERMINATED which are added by the client.
 * The LongOp.State statuses (which are obscured from the graphQL schema):
 *   DONE
 *   RUNNING
 *   PENDING
 *   PAUSED
 **/
export enum QueryStatus {
  Running     = 'RUNNING',
  Terminated  = 'TERMINATED', // Terminated by error (network or server response)
  Canceled    = 'CANCELED', // Canceled manually by user
  Done        = 'DONE'
}

export const NOTABLE_CHANGE_RANGES = {
  TRAFFIC: {
    min: 80,
    max: 100,
  },
  LATENCY: {
    min: 5,
    max: 40,
  },
  ERRORS: {
    min: 5,
    max: 30,
  },
};

/**
 * Global state
 */

// sorted list of global keys
//
// Note: The use of reactn and GLOBAL_KEY for global state is deprecated. Please use recoil and RECOIL_ATOM_KEY or RECOIL_SELECTOR_KEY
export enum GLOBAL_KEY {
  CHART_SELECTION_RANGE                   = 'chartSelectionRange',
  DASHBOARD                               = 'dashboard',
  DASHBOARD_FACET_CACHE_IDS               = 'dashboardFacetCacheIds',
  DEFAULT_START_TIME                      = 'defaultStartTime',
  ELEMENT_HEIGHT_STATE                    = 'elementHeightState',                  // height of each element observed by the ResizeObserver
  ELEMENT_WIDTH_STATE                     = 'elementWidthState',                   // width of each element observed by the ResizeObserver
  FACET_DATA                              = 'facetData',                           // data from FacetQuery
  FACET_INFO                              = 'facetInfo',                           // facet info useful for autocomplete (SearchHelper)
  GRAPH_SERIES_DATA                       = 'graphSeriesData',
  LABS                                    = 'labs',
  LOG_ENTRY_ASC_THRESHOLDS                = 'logEntryAscThresholds',
  LOG_ENTRY_DESC_THRESHOLDS               = 'logEntryDescThresholds',
  LOG_ENTRY_MEASUREMENT_CACHE             = 'logEntryMeasurementCache',
  LOG_ENTRY_PAGINATION_CURSORS            = 'logEntryPaginationCursors',
  LOG_ID_LIST                             = 'logIdList',
  LOGS_BY_ID                              = 'logsById',
  MATCH_COUNT                             = 'matchCount',
  PAGE                                    = 'page',
  PLOT_DATA                               = 'plotData',
  RESIZE_OBSERVER_STATE                   = 'resizeObserverState',                 // global singleton ResizeObserver instance
  SAVED_SETTINGS                          = 'savedSettings',                       // settings on server
  SELECTED_TOKEN_STATE                    = 'selectedTokenState',
  SETTINGS                                = 'settings',                            // settings in memory
  SETTINGS_PROCESSED                      = 'settingsProcessed',
  TOGGLE_SIDEBAR_REF_STATE                = 'toggleSidebarRef',
  WINDOW_HEIGHT_STATE                     = 'windowHeightState',                   // height of the global window object
  WINDOW_WIDTH_STATE                      = 'windowWidthState',                    // width of the global window object
  TIME_RANGE_DROPDOWN_STATE               = 'timeRangeDropdownState',              // Recoil state key for TimeRangeDropdown functionality
}

// keys of recoil atoms https://recoiljs.org/docs/api-reference/core/atom
export enum RECOIL_ATOM_KEY {
  ACTIVE_REQUEST_TIME                     = 'activeRequestTime',
  ACTIVE_SEARCH_MATCH_COUNT_ESTIMATE      = 'activeSearchMatchCountEstimate',
  ALERTS_CONFIG                            = 'alertsConfig',
  ANCHOR_CURSOR                           = 'anchorCursor',
  ANCHOR_LOG_REQUESTS                     = 'anchorLogRequests',
  AUTO_REFRESH_MINUTES                    = 'autoRefreshMinutes',
  BAR_CHART_DISPLAY_OPTIONS               = 'barChartDisplayOptions',
  COUNT_PER_TAB_REQUEST                   = 'countPerTabRequest',
  CURRENT_CHART_REF                       = 'currentChartRef',
  DRAFT_SEARCH_PARAMS_BY_ROUTE            = 'draftSearchParamsByRoute',
  DASHBOARD_FACET_VALUES_REQUESTS         = 'dashboardFacetValuesRequests',
  DASHBOARD_TABS                          = 'dashboardTabs',
  DEBUG                                   = 'debug',
  DEFAULT_VIEW                            = 'defaultView',
  DETAILS_DATA                            = 'detailsData',
  EVENT_CACHE_WATCHER                     = 'eventCacheWatcher',
  EVENT_PARAM_RECORD                      = 'eventParamRecord',
  FACET_INFO_FROM_DATA                    = 'facetInfoFromData',
  FACET_INFO                              = 'facetInfo',
  FACET_INFO_NEEDED                       = 'facetInfoNeeded',
  FACET_VALUES_REQUESTS                   = 'facetValuesRequests',
  HINDSIGHT_RETENTION                     = 'hindsightRetention',
  INITIAL_LOG_REQUESTS                    = 'initialLogRequests',
  IS_CROSS_TEAM_SEARCH                    = 'isCrossTeamSearch',
  IS_FULL_SCREEN_MODE                     = 'isFullScreenMode',
  IS_LIVE_TAIL_ACTIVE                     = 'isLiveTailActive',
  LINE_CHART_DISPLAY_OPTIONS              = 'lineChartDisplayOptions',
  LOCATION                                = 'location',
  LOG_RESULTS_DOWN_THRESHOLD_STATE        = 'logResultsDownThresholdState',
  LOG_RESULTS_UP_THRESHOLD_STATE          = 'logResultsUpThresholdState',
  LOG_RESULTS_SCROLL_EXTREMES             = 'logResultsScrollExtremes',
  LOG_RESULTS_SCROLL_OFFSET               = 'logResultsScrollOffset',
  LOG_RESULTS_SCROLL_TIME_MARKER          = 'logResultsScrollTimeMarker',
  LOG_RESULTS_SCROLL_READY                = 'logResultsScrollReady',
  LOG_TO_FETCH                            = 'logToFetch',                          // info about log to fetch via 'getLogEntry'
  LOG_TO_INSPECT                          = 'logToInspect',                        // log entry (just fetched) to show in LogDetails inspector
  LRQ_ERRORS_BY_ID                        = 'lrqErrorsById',
  NETWORK_ERROR                           = 'networkError',
  ORIGIN                                  = 'origin',
  PAGE_ALERT_CONFIG                       = 'pageAlertConfig',
  PAGINATED_LOG_REQUESTS                  = 'paginatedLogRequests',
  PLOT_DATA_REQUESTS                      = 'plotDataRequests',
  POWER_QUERY_REQUESTS                    = 'powerQueryRequests',                  // combined search page (includes facets request)
  PREFERENCES                             = 'preferences',
  PREVENT_LOG_INSPECTOR_FROM_CLOSING      = 'preventLogInspectorFromClosing',      // needed to allow dialogs launched from log inspector to be closed
  RAW_DASHBOARD                           = 'rawDashboard',                        // unprocessed dashboard config from the server
  ROUTE                                   = 'route',
  SEARCH_PARAM                            = 'searchParam',
  SEARCH_RESULTS_MENU_ITEMS               = 'searchResultsMenuItems',
  SEE_MORE                                = 'seeMore',
  SELECTED_FACET                          = 'selectedFacet',
  SHOW_FACET_DETAILS                      = 'showFacetDetails',
  TEAM_EMAILS                             = 'teamEmails',
  TEAM_TOKEN                              = 'teamToken',
  UPDATE_TEAM_NAME_DIALOG                 = 'updateTeamNameDialog',
  USER_DATA                               = 'userData',                            // data needed by navbar dropdowns and elsewhere
  VIEW_SETTINGS                           = 'viewSettings',
  VISIBLE_CENTER_INDEX                    = 'visibleCenterIndex',
  WRAPPED_LINES_SCROLLBAR_WIDTH           = 'wrappedLinesScrollbarWidth',

  // Major Components Statuses
  SIDEBAR_STATUS                          = 'sidebarStatus',
  LOG_RESULTS_TIMELINE_STATUS             = 'logResultsTimelineStatus',
  GRAPH_RESULTS_STATUS                    = 'graphResultsStatus',
  LOG_RESULTS_STATUS                      = 'logResultsStatus',
  PIN_INSPECT_LOG_DETAILS                 = 'pinInspectLogDetails',
  SHOW_DATA_DETAILS                       = 'showDataDetails',
}

// keys of recoil selectors (derived state from recoil atoms) https://recoiljs.org/docs/api-reference/core/selector
export enum RECOIL_SELECTOR_KEY {
  ABSOLUTE_RESOLVED_URL                   = 'absoluteResolvedUrl',
  ACCOUNT_DATA                            = 'accountData',
  ACCOUNT_DATA_READY                      = 'accountDataReady',
  ACTIVE_DATA_SOURCES                     = 'activeDataSources',
  ACTIVE_SEARCH_MATCH_COUNT               = 'activeSearchMatchCount',
  ACTIVE_TEAM                             = 'activeTeam',
  AVAILABLE_DATA_SOURCES                  = 'availableDataSources',
  AVAILABLE_NONACTIVE_TEAMS               = 'availableNonactiveTeams',
  AVAILABLE_ALL_TEAMS_EMAIL_ADDRESSES     = 'availableAllTeamsEmailAddresses',
  BULK_EXPORTS_PAGE_URL                   = 'bulkExportsPageUrl',
  CELL_RENDERER                           = 'cellRenderer',
  COMPLETE_FILTER                         = 'completeFilter',
  COMPLETE_FILTER_WITHOUT_TABS            = 'completeFilterWithoutTabs',
  COMPLETE_POWER_QUERY                    = 'completePowerQuery',
  CURRENT_DASHBOARD                       = 'currentDashboard',
  DASHBOARD                               = 'dashboard',                // processed dashboard entity with param values
  DASHBOARD_LIST                          = 'dashboardList',
  DASHBOARD_FILTER_CONFIG_LIST            = 'dashboardFilterConfigList',
  DASHBOARD_URL                           = 'dashboardUrl',
  DASHBOARDS_ACCESSIBLE                   = 'dashboardsAccessible',
  DATA_DETAILS_CARD_RENDERER              = 'dataDetailsCardRenderer',
  DEFAULT_VIEW_SETTING                    = 'defaultViewSetting',
  DETAILS_DATA_MANAGER                    = 'detailsDataManager',
  DISPLAY_MODE                            = 'displayMode',
  DRAFT_SEARCH_PARAMS                     = 'draftSearchParams',
  EVENT_PARAMS                            = 'eventParams',
  EVENT_SORT_ORDER                        = 'eventSortOrder',
  FACET_LIST                              = 'facetList',
  FEATURE_PERMISSIONS                     = 'featurePermissions',       // flag that says to run search even if query is unchanged
  FEATURE_PERMISSIONS_READY               = 'featurePermissionsReady',
  HAS_PERMISSION                          = 'hasPermission',
  HAS_TEAMS                               = 'hasTeams',
  HOME_PAGE                               = 'homePage',                 // home page url
  HOME_PAGE_AVAILABLE_SECTIONS            = 'homePageAvailableSections',
  HOME_PAGE_EXCLUDING_CURRENT             = 'homePageExcludingCurrent',
  HOME_PAGE_IS_VALID                      = 'homePageIsValid',
  HOME_PAGE_USER_TEAM                     = 'homePageUserTeam',
  INITIAL_DASHBOARD_TAB_NAMES             = 'initialDashboardTabNames',
  IS_AT_END_TIME                          = 'isAtEndTime',
  IS_AT_START_TIME                        = 'isAtStartTime',
  IS_FACET_DETAILS_VISIBLE                = 'isFacetDetailsVisible',
  IS_LAB_ENABLED                          = 'isLabEnabled',
  IS_MANAGE_GROUPS_ENABLED                = 'isManageGroupsEnabled',
  IS_ROUTE_ENABLED                        = 'isRouteEnabled',           // is the current route enabled for this user
  IS_ROUTER_READY                         = 'isRouterReady',            // the router and its children are ready to render
  IS_SHOW_THREAD_OR_ORIGINAL_LOG          = 'isShowThreadOrOriginalLog',
  IS_SIDEBAR_TOGGLE_VISIBLE               = 'isSidebarToggleVisible',
  IS_STANDARD_VIEW                        = 'isStandardView',
  IS_TEAM_DISPLAYED                       = 'isTeamDisplayed',
  IS_VIEWING_OWN_ACCOUNT                  = 'isViewingOwnAccount',
  LABS                                    = 'labs',
  LIVE_TAIL_PAGE_URL                      = 'liveTailPageUrl',
  LOG_DETAILS_MODE                        = 'logDetailsMode',
  LOG_DETAILS_RENDERER                    = 'logDetailsRenderer',
  LOG_SEARCH_MATCH_COUNT_TITLE            = 'logSearchMatchCountTitle',
  LOGO                                    = 'logo',
  LOGS_ACCESSIBLE                         = 'logsAccessible',
  NAVBAR_VARIANT                          = 'navbarVariant',            // Bootstrap
  PERMISSION_RECORD                       = 'permissionRecord',
  PIN_LOG_DETAILS                         = 'pinLogDetails',
  PLUGIN                                  = 'plugin',
  POWER_QUERIES_PAGE_URL                  = 'powerQueriesPageUrl',
  PREFILTER                               = 'prefilter',
  QUERY_TIME_RANGE                        = 'queryTimeRange',
  QUICK_SWITCH_LINK_ACCOUNT_PAGE_URL      = 'quickSwitchLinkAccountPageUrl',
  RAW_DASHBOARD_FILTER_CONFIG             = 'rawDashboardFilterConfig',
  RECENT_NONACTIVE_TEAMS                  = 'recentNonactiveTeams',
  RESULTS_TAB                             = 'resultsTab',
  RESULTS_TAB_URL                         = 'resultsTabUrl',
  RESULTS_TABS                            = 'resultsTabs',
  RESULTS_TABS_WITH_FILTER                = 'resultsTabsWithFilter',
  RETENTION_PERIOD                        = 'retentionPeriod',          // short-term retention period for data
  SAVED_SEARCHES                          = 'savedSearches',
  SCHEMA                                  = 'schema',                   // schema for a Skylight datasource
  SEARCH_ACCESSIBLE                       = 'searchAccessible',
  SCHEMA_FIELDS                           = 'schemaFields',             // fields part of schema
  SEARCH_GRAPH_LEGEND_WIDTH               = 'searchGraphLegendWidth',
  SEARCH_GRAPH_PAGE_REMAINING_WIDTH       = 'searchGraphPageRemainingWidth',
  SEARCH_GRAPH_PAGE_STATUSES              = 'searchGraphPageStatuses',  // is used to get status per each major search graph page component
  SEARCH_LOGS_PAGE_URL                    = 'searchLogsPageUrl',
  SEARCH_PAGE_IS_PENDING                  = 'searchPageIsPending',      // is used to track if the page components are still loading(pending) or resolved (e.g. to display some "In progress" UI)
  SEARCH_PAGE_REMAINING_WIDTH             = 'searchPageRemainingWidth', // remaining width to the right of the sidebar on the search logs page
  SEARCH_PAGE_STATUSES                    = 'searchPageStatuses',       // is used to get status per each major search page component
  SEARCH_PARAM_RECORD                     = 'searchParamRecord',
  SEARCH_SIDEBAR_WIDTH                    = 'searchSidebarWidth',
  TEAM_SWITCH_URL                         = 'teamSwitchUrl',            // current relative url les team token
  TEAM_EMAILS_FROM_PARAMS                 = 'teamEmailsFromParams',
  TEAM_EMAILS_STRING_FROM_PARAMS          = 'teamEmailsStringFromParams',
  TEAMS                                   = 'teams',
  THEME                                   = 'theme',
  TIMEZONE                                = 'timezone',
  TIMEZONE_CHANGE_REQUIRES_NEW_SEARCH     = 'timezoneChangeRequiresNewSearch',
  UNPIN_LOG_DETAILS                       = 'unPinLogDetails',
  USER_MENU_DISPLAY_NAME                  = 'userMenuDisplayName',
  VIEW                                    = 'view',
  VIEW_DISPLAY_SETTINGS                   = 'viewDisplaySettings',
  VIEW_FIELDS                             = 'viewFields',
  VIEW_URL                                = 'viewUrl',
  WRAPPED_LINE_WIDTH                      = 'wrappedLineWidth',
}

// unique ids for elements to use the useResizeRegistration hook
export enum RESIZE_ID {
  SEARCH_LOGS_SIDEBAR  = 'searchLogsSidebar',
  SEARCH_GRAPHS_LEGEND = 'searchGraphsLegend',
  SEARCH_LOG_DETAILS   = 'searchLogDetails',
}

// Unique ids for page alerts. Useful for identifying a particular alert since its content might be updated immutably.
export enum PAGE_ALERT_ID {
  SEARCH_PROGRESS =    'searchProgress',
  SEARCH_TIMEOUT =     'searchTimeout',
  SEARCHING =          'searching',
}

export const NO_PAGE_ALERT = { message: '' };

// sorted list of global default values
export const GLOBAL_DEFAULT = {
  [GLOBAL_KEY.CHART_SELECTION_RANGE]:        EMPTY_OBJECT,
  [GLOBAL_KEY.DASHBOARD_FACET_CACHE_IDS]:    EMPTY_ARRAY,
  [GLOBAL_KEY.GRAPH_SERIES_DATA]:            EMPTY_ARRAY,
  [GLOBAL_KEY.LOG_ENTRY_ASC_THRESHOLDS]:     EMPTY_ARRAY,
  [GLOBAL_KEY.LOG_ENTRY_DESC_THRESHOLDS]:    EMPTY_ARRAY,
  [GLOBAL_KEY.LOG_ENTRY_MEASUREMENT_CACHE]:  EMPTY_OBJECT,
  [GLOBAL_KEY.LOG_ENTRY_PAGINATION_CURSORS]: EMPTY_ARRAY,
};

export const HONEYCOMB_COLOR_OPTIONS = [
  { label: 'Blue', value: '#0998E7', hover: "#8ED4FB"},
  { label: 'Green', value: '#6DB222', hover: "#B9E885" },
  { label: 'Purple', value: '#9978FF' , hover: "#D3C4FF"},
  { label: 'Red', value: '#DA372F', hover: "#ED9F9B"},
];

export const DEFAULT_HONEYCOMB_THRESHOLD_VALUES = ["0","25","50","75"];
export const HONEYCOMB_THRESHOLD_OPACITY = [0.25, 0.5, 0.75, 1];

export enum PASSWORD_STRENGTH {
  UNDEFINED,
  WEAK,
  MODERATE,
  STRONG,
  INVALID,
}

export enum DIALOG_FORM_IDS {
  ADD_GRAPH_MODAL_FORM_ID                   = 'addGraphModal__Form',
  ADD_EDIT_PLOTS_MODAL_FORM_ID              = 'addEditPlotsModal__Form',
  DASHBOARD_SETTINGS_MODAL_FORM_ID          = 'dashboardSettings_Form',
}

export enum BILLING_TYPE {
  CHANNEL        = 'channel',
}

// in server's processing order
export enum ACCEPT_LINK_PARSE_TOKEN_ERRORS {
  // missing invitation url
  missingInvitationUrl = 'missingInvitationUrl',
  // token not found
  token_error = 'token',
  // bad invitation url - actually currently it cannot happen, because token_error pops first
  badInvitationUrl = 'badInvitationUrl',
  // currently logged user opened invite for another account - should logout
  incorrectInvitee = 'incorrectInvitee',
  // invite was issued for already created user - user should login to accept invite
  inviteRequiresLogin = 'inviteRequiresLogin',
  // invite for such account has never been sent
  invalidInvitee = 'invalidInvitee',
  // invite has expired
  expiredInvite = 'expiredInvite'
}

export const NUM_EX = /\d/;
export const LC_EX  = /[a-z]/;
export const UC_EX  = /[A-Z]/;
export const SY_EX  = /\W/;

export const CHART_PLOT_LINES_ZINDEX = 4;
export const CHART_PLOT_LINES_WIDTH = 1;
export const CHART_PLOT_LINES_DASHSTYLE = 'Dash';
export const CHART_SELECTION_ID = 'Selection';

export const ACCESS_DENIED = 'accessDenied';

// GraphQL operation name to send logs to Scalyr
export const LOG_TO_SCALYR = 'LogToScalyr';

export const DEFAULT_ALERTS_ADDRESS_FIELD = 'defaultAlertAddress'
export const DEFAULT_ALERTS_CONFIG = {
  [DEFAULT_ALERTS_ADDRESS_FIELD]: ''
}

// Kubernetes Explorer built in dashboards
export const UNHEALTHY_STATUS = 'Unhealthy';
export const STATUS = 'Status';
export const KUBERNETES_EXPLORER                  = 'Kubernetes explorer'; // built-in dashaboard name
export const WORKLOAD_TAB                         = 'WORKLOADS';
export const WORKLOADS_CELL_LINK_SEPARATOR        = '#@#';
export const WORKLOADS_LINK_HEADER                = 'Workload';

// This is used to explicity set that no team is selected in CrossTeamSearch
export const NO_TEAM_EMAILS_PARAM = '-';
